import React, { memo, useCallback } from 'react';
import { formatNumber } from '@atc/string-fns';
import 'prop-types';
import clsx from 'clsx';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { DelayedImpressionObserver } from 'reaxl-analytics';
import { Row, Col, Image, LoadingSpinner, Text, Glyphicon, AccessibilityUtilities } from 'reaxl';
import { useFeatures } from '@atc/features';
import { ratesLabels } from 'axl-config';
import { useDevice } from '@atc/react-device';
import WalletSymbol from '../WalletSymbol/WalletSymbol.js';
import MyWalletGridTiles from './MyWalletGridTiles.js';
import DynamicIcon from './DynamicIcon.js';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
const {
  withKeyboardAccessibility
} = AccessibilityUtilities;
const defaultTitle = function () {
  let headline = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
  return /*#__PURE__*/jsx("span", {
    className: "text-link link-undecorated padding-vertical-1 display-inline-block",
    children: headline
  });
};
const baseIconUrl = 'https://www.autotrader.com/content/static/img/icon';
const baseLogosUrl = baseIconUrl + '/logos';
const baseGridIconUrl = `${baseIconUrl}/myWallet/grid`;
const desktopContentHeight = 50;
const lessThanDesktopHeight = 68;
function determineElementHeight(_ref) {
  let {
    updatedGrid,
    isDesktop
  } = _ref;
  return updatedGrid ? {
    height: isDesktop ? `${desktopContentHeight}px` : `${lessThanDesktopHeight}px`
  } : undefined;
}
const tradeInWithKbbLogoHeadline = _ref2 => {
  let {
    title,
    updatedGrid,
    displayValue,
    showIcon = true,
    isDesktop
  } = _ref2;
  return /*#__PURE__*/jsxs("div", {
    className: "row",
    style: determineElementHeight({
      updatedGrid,
      isDesktop
    }),
    children: [/*#__PURE__*/jsx("div", {
      className: clsx({
        'col-lg-4': updatedGrid,
        'margin-top-lg-2': updatedGrid
      }),
      children: showIcon && /*#__PURE__*/jsx("img", {
        height: "40",
        width: "28",
        src: `${baseLogosUrl}/kbb-logo-notext.svg`,
        alt: "Trade-in with KBB"
      })
    }), /*#__PURE__*/jsxs("div", {
      className: clsx({
        'text-lg-left': updatedGrid,
        'col-lg-8': updatedGrid
      }, 'margin-top-1 '),
      children: [/*#__PURE__*/jsx("div", {
        className: "hidden-xs hidden-sm hidden-md",
        children: /*#__PURE__*/jsx("div", {
          className: clsx({
            'hidden-lg': !updatedGrid
          }, 'hidden-xs hidden-sm hidden-md'),
          children: /*#__PURE__*/jsx("div", {
            className: "padding-bottom-1 text-bold text-size-200 text-size-sm-100 text-subdued-lighter",
            children: title.toUpperCase()
          })
        })
      }), displayValue]
    })]
  });
};
const renderUpdatedGridHeadlineTemplate = _ref3 => {
  let {
    logo,
    title,
    subLabel,
    iconUrl,
    showIcon = true,
    isDesktop,
    updatedGrid
  } = _ref3;
  const dynamicIcon = ['credit_&_term', 'monthly_payment', 'down_payment', 'mileage'];
  return /*#__PURE__*/jsxs("div", {
    className: "row",
    style: determineElementHeight({
      updatedGrid,
      isDesktop
    }),
    children: [/*#__PURE__*/jsx("div", {
      className: "col-lg-4",
      children: showIcon && /*#__PURE__*/jsx("div", {
        className: "col-lg-4 margin-top-lg-2",
        children: dynamicIcon.includes(logo) ? /*#__PURE__*/jsx(DynamicIcon, {
          logo: logo
        }) : /*#__PURE__*/jsx("img", {
          height: "40",
          width: "40",
          src: `${iconUrl}/${logo}.svg`,
          alt: ""
        })
      })
    }), /*#__PURE__*/jsxs("div", {
      className: "col-lg-8 margin-top-1 text-lg-left",
      children: [/*#__PURE__*/jsx("div", {
        className: "hidden-xs hidden-sm hidden-md",
        children: /*#__PURE__*/jsx("div", {
          className: "padding-bottom-1 text-bold text-size-200 text-size-sm-100 text-subdued-lighter",
          children: title.toUpperCase()
        })
      }), subLabel]
    })]
  });
};
const renderBudgetWithLogoHeadline = (logo, subLabel, iconUrl, showIcon) => /*#__PURE__*/jsxs("div", {
  className: "row",
  children: [/*#__PURE__*/jsx("div", {
    children: showIcon && /*#__PURE__*/jsx("img", {
      height: "44",
      width: "35",
      src: `${iconUrl}/${logo}.svg`,
      alt: "Updated Grid Logo"
    })
  }), /*#__PURE__*/jsx("div", {
    className: "text-link link-undecorated margin-top-1",
    children: subLabel
  })]
});
const renderDefaultUpdatedGridHeadline = _ref4 => {
  let {
    logo,
    title,
    subLabelText,
    iconUrl,
    showIcon,
    isDesktop,
    updatedGrid
  } = _ref4;
  const subLabel = /*#__PURE__*/jsx("div", {
    className: "text-link link-undecorated margin-top-1",
    style: {
      marginBottom: '-4px'
    },
    children: subLabelText
  });
  return renderUpdatedGridHeadlineTemplate({
    logo,
    title,
    subLabel,
    iconUrl,
    showIcon,
    isDesktop,
    updatedGrid
  });
};
const renderUpdatedGridHeadlineWithData = _ref5 => {
  let {
    logo,
    title,
    value,
    isCurrency = false,
    iconUrl,
    showIcon,
    isDesktop,
    updatedGrid
  } = _ref5;
  const subLabel = /*#__PURE__*/jsxs(Fragment, {
    children: [isCurrency && /*#__PURE__*/jsx(WalletSymbol, {
      symbol: "$"
    }), /*#__PURE__*/jsx(Text, {
      color: "accent",
      weight: "bold",
      children: value
    }), title !== 'kbbTrademark' && /*#__PURE__*/jsx(Glyphicon, {
      glyph: "edit",
      className: "text-subdued-lighter margin-left-2"
    })]
  });
  return renderUpdatedGridHeadlineTemplate({
    logo,
    title,
    subLabel,
    iconUrl,
    showIcon,
    isDesktop,
    updatedGrid
  });
};
const renderTradeInHeadline = _ref6 => {
  let {
    tradeInValue,
    netTradeInValue,
    useKbbLogoTradeIn,
    updatedGrid,
    source,
    showIcon,
    isDesktop
  } = _ref6;
  if (tradeInValue !== null && tradeInValue >= 0) {
    const subLabel = /*#__PURE__*/jsxs(Fragment, {
      children: [/*#__PURE__*/jsx(WalletSymbol, {
        symbol: "$"
      }), /*#__PURE__*/jsx(Text, {
        color: "accent",
        weight: "bold",
        children: formatNumber({
          value: netTradeInValue
        })
      }), /*#__PURE__*/jsx(Glyphicon, {
        glyph: "edit",
        className: "text-subdued-lighter margin-left-2"
      })]
    });
    if (updatedGrid && source !== 'KBB') {
      return tradeInWithKbbLogoHeadline({
        title: 'TRADE-IN VALUE',
        updatedGrid,
        displayValue: subLabel,
        showIcon,
        isDesktop
      });
    }
    if (updatedGrid && source === 'KBB') {
      return /*#__PURE__*/jsxs(Fragment, {
        children: [/*#__PURE__*/jsx("div", {
          className: "hidden-lg",
          children: renderUpdatedGridHeadlineWithData({
            logo: 'kbb-logo-notext',
            title: 'kbbTrademark',
            value: subLabel,
            isCurrency: false,
            iconUrl: baseLogosUrl,
            showIcon,
            isDesktop,
            updatedGrid
          })
        }), /*#__PURE__*/jsx("div", {
          className: "hidden-xs hidden-sm hidden-md",
          children: subLabel
        })]
      });
    }
    return formatNumber({
      value: netTradeInValue
    });
  }
  if (useKbbLogoTradeIn || updatedGrid) {
    const defaultDisplayValue = /*#__PURE__*/jsx("div", {
      className: "text-link link-undecorated margin-top-1",
      style: {
        marginBottom: '-4px'
      },
      children: "Add a vehicle"
    });
    return tradeInWithKbbLogoHeadline({
      title: `TRADE-IN ${updatedGrid ? 'VALUE' : ''}`,
      updatedGrid,
      displayValue: defaultDisplayValue,
      showIcon,
      isDesktop
    });
  }
  return defaultTitle('Add a vehicle');
};
const getItems = function () {
  let items = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  return items.map((_ref7, index) => {
    let {
      title = '',
      headline = '',
      globalWalletTheme,
      onClick = () => {},
      isCurrency,
      headlineClassName,
      source,
      addPadding,
      ...rest
    } = _ref7;
    return {
      key: `my-wallet-modal-button-${index}`,
      // add padding to Trade in tile to extend container length
      className: clsx('text-center', {
        'padding-bottom-5': addPadding,
        'global-theme-enabled': globalWalletTheme
      }),
      category: title,
      headline: /*#__PURE__*/jsxs(Fragment, {
        children: [isCurrency && /*#__PURE__*/jsx(WalletSymbol, {
          size: 500,
          symbol: "$"
        }), /*#__PURE__*/jsx("span", {
          className: headlineClassName,
          children: headline
        })]
      }),
      onClick,
      onKeyDown: withKeyboardAccessibility(onClick),
      ...rest
    };
  });
};
const renderButtonTitle = function () {
  let title = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
  let useKbbLogoTradeIn = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  let updatedGrid = arguments.length > 2 ? arguments[2] : undefined;
  return /*#__PURE__*/jsx("div", {
    className: clsx({
      'hidden-lg': updatedGrid,
      'padding-bottom-2': !useKbbLogoTradeIn,
      'padding-bottom-1': useKbbLogoTradeIn,
      'text-size-200': updatedGrid,
      'text-subdued-lighter': updatedGrid,
      'text-gray': !updatedGrid
    }, 'text-bold text-size-100 text-size-sm-100'),
    children: title.toUpperCase()
  });
};
// contains variations in styles based on if it's being used on the updated SRP grid
const renderKbbBadge = function () {
  let updatedGrid = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
  return /*#__PURE__*/jsxs(Row, {
    className: "justify-content-center display-flex",
    children: [/*#__PURE__*/jsx(Col, {
      className: clsx({
        'hidden-md': updatedGrid,
        'hidden-sm': updatedGrid,
        'hidden-xs': updatedGrid
      }),
      children: /*#__PURE__*/jsx(Image, {
        height: 40,
        width: 40,
        src: `${baseLogosUrl}/kbb-logo-notext.svg`,
        className: "margin-horizontal-2 margin-top-1"
      })
    }), /*#__PURE__*/jsxs(Col, {
      className: clsx('text-size-100', {
        'text-subdued-lighter': updatedGrid,
        'text-gray': !updatedGrid
      }),
      children: [/*#__PURE__*/jsx("div", {
        className: "text-bold",
        children: "KELLEY BLUE BOOK\xAE"
      }), /*#__PURE__*/jsx("div", {
        className: clsx({
          'text-lg-left': updatedGrid,
          'text-left': !updatedGrid
        }, 'text-bold'),
        children: "TRADE-IN VALUE"
      })]
    })]
  });
};
const MyWalletGrid = /*#__PURE__*/memo(_ref8 => {
  let {
    isRow = false,
    icoEnabled,
    paymentsInfo = {},
    paymentOption,
    onClick = () => {},
    onTradeInIndicatorImpression,
    creditVerificationInfo,
    updatedGrid = false,
    hideDefaultsPreEngagement = false,
    isLoading = false,
    isPaymentsInfoLoaded,
    showIcon = true,
    ...rest
  } = _ref8;
  const {
    global_kbb_branded_wallet: [enableKbbBrandedWallet, enableWalletVariants],
    srp_wallet_grid_update: [, {
      iconUrlFolder: iconFolder
    }],
    wallet_payment_option: [enablePaymentOption],
    wallet_tile_arrangement: [enableWalletTileArrangement]
  } = useFeatures(['global_kbb_branded_wallet', 'wallet_tile_arrangement', 'srp_wallet_grid_update', 'wallet_payment_option']);
  const device = useDevice();
  const isDesktop = device?.is?.lg;
  const iconUrl = iconFolder ? baseGridIconUrl + '/' + iconFolder : baseGridIconUrl;
  const enableKbbBrandedWalletWithoutVariants = enableKbbBrandedWallet && (_isEmpty(enableWalletVariants) || !Object.values(enableWalletVariants).some(Boolean));
  const {
    kbb_logo_trade_in: useKbbLogoTradeIn
  } = enableWalletVariants;
  const {
    creditType,
    downPayment,
    downPaymentPercent,
    tradeIn = {},
    interactions: {
      hasOpenedTradeIn,
      hasUpdatedWallet
    } = {},
    budget,
    monthlyBudget,
    leaseMileageRange
  } = paymentsInfo;
  const {
    value: tradeInValue,
    amountOwed = 0,
    ico = [],
    source,
    hasNewTradeIn
  } = tradeIn;
  const isLeasePaymentOptionSelected = enablePaymentOption && paymentOption === 'lease';
  const creditTypeLabel = _get(ratesLabels, [creditType], ratesLabels.a);
  const creditVerifiedValue = creditVerificationInfo?.creditRating;
  const creditVerifiedDate = new Date(creditVerificationInfo?.verifiedDate);
  const showCreditVerifiedLabel = creditType === creditVerifiedValue;
  const swapElements = (array, firstElement, secondElement) => {
    // eslint-disable-next-line prefer-destructuring
    array[firstElement] = array.splice(secondElement, 1, array[firstElement])[0];
  };

  // null check needed here so we dont incorrectly display '$0' as '0%'
  const hasDownpaymentPercentInfo = !downPayment && downPayment !== 0 && downPaymentPercent !== null && downPaymentPercent >= 0 && downPaymentPercent <= 1;
  const renderDownPaymentHeadline = () => {
    if (hasDownpaymentPercentInfo) {
      const downPaymentDisplayValue = `${Math.round(downPaymentPercent * 100)}%`;
      if (updatedGrid) {
        if (hideDefaultsPreEngagement && !hasUpdatedWallet) {
          return renderDefaultUpdatedGridHeadline({
            logo: 'down_payment',
            title: 'Down Payment',
            subLabelText: 'Enter an amount',
            iconUrl,
            showIcon,
            isDesktop,
            updatedGrid
          });
        }
        return renderUpdatedGridHeadlineWithData({
          logo: 'down_payment',
          title: 'Down Payment',
          value: downPaymentDisplayValue,
          isCurrency: false,
          iconUrl,
          showIcon,
          isDesktop,
          updatedGrid
        });
      }
      return downPaymentDisplayValue;
    }
    if (updatedGrid) {
      return renderDefaultUpdatedGridHeadline({
        logo: 'down_payment',
        title: 'Down Payment',
        subLabelText: 'Enter an amount',
        iconUrl,
        showIcon,
        isDesktop,
        updatedGrid
      });
    }
    return defaultTitle('Get Started');
  };
  const downPaymentComponentProps = {
    headline: renderDownPaymentHeadline(),
    isCurrency: false,
    headlineClassName: hasDownpaymentPercentInfo && !updatedGrid ? 'text-size-500 text-accent text-bold' : ''
  };
  if (!hasDownpaymentPercentInfo && downPayment >= 0) {
    if (updatedGrid) {
      if (hideDefaultsPreEngagement && !hasUpdatedWallet) {
        downPaymentComponentProps.headline = renderDefaultUpdatedGridHeadline({
          logo: 'down_payment',
          title: 'Down Payment',
          subLabelText: 'Enter an amount',
          iconUrl,
          showIcon,
          isDesktop,
          updatedGrid
        });
      } else {
        downPaymentComponentProps.headline = renderUpdatedGridHeadlineWithData({
          logo: 'down_payment',
          title: 'Down Payment',
          value: formatNumber({
            value: downPayment
          }),
          isCurrency: true,
          iconUrl,
          showIcon,
          isDesktop,
          updatedGrid
        });
      }
    } else {
      downPaymentComponentProps.headline = formatNumber({
        value: downPayment
      });
      downPaymentComponentProps.isCurrency = true;
      downPaymentComponentProps.headlineClassName = 'text-size-500 text-accent text-bold';
    }
  }
  const netTradeInValue = tradeInValue - amountOwed;
  const renderBudgetHeadline = () => {
    if (budget !== null && budget >= 0) {
      if (updatedGrid) {
        return renderUpdatedGridHeadlineWithData({
          logo: 'monthly_payment',
          title: 'Budget',
          value: `${formatNumber({
            value: monthlyBudget || 0
          })}/mo.`,
          isCurrency: true,
          iconUrl,
          showIcon,
          isDesktop,
          updatedGrid
        });
      }
      if (isLeasePaymentOptionSelected) {
        return /*#__PURE__*/jsx(Text, {
          size: 500,
          color: "accent",
          weight: "bold",
          children: formatNumber({
            value: budget || 0
          })
        });
      }
      return /*#__PURE__*/jsxs(Fragment, {
        children: [/*#__PURE__*/jsx(Text, {
          size: 500,
          color: "accent",
          weight: "bold",
          children: formatNumber({
            value: monthlyBudget || 0
          })
        }), /*#__PURE__*/jsx(Text, {
          size: 400,
          color: "accent",
          children: "/mo."
        }), /*#__PURE__*/jsx(Text, {
          size: 100,
          className: "display-block padding-top-1",
          color: "subdued-lighter",
          children: "Estimated Payment"
        })]
      });
    }
    if (updatedGrid) {
      return renderDefaultUpdatedGridHeadline({
        logo: 'monthly_payment',
        title: 'Budget',
        subLabelText: 'Set a budget',
        iconUrl,
        showIcon,
        isDesktop,
        updatedGrid
      });
    }
    if (enableWalletTileArrangement) {
      return renderBudgetWithLogoHeadline('monthly_payment', 'Set a budget', iconUrl, showIcon);
    }
    return defaultTitle('Set a Budget');
  };
  const renderCreditVerified = () => showCreditVerifiedLabel && /*#__PURE__*/jsxs("div", {
    className: "padding-top-1",
    children: [/*#__PURE__*/jsx(Glyphicon, {
      className: "text-success",
      glyph: "checkbox-selected"
    }), /*#__PURE__*/jsxs("span", {
      className: "padding-left-1 text-size-200",
      children: ["Verified on ", creditVerifiedDate.toLocaleDateString('en-US')]
    })]
  });
  const renderCreditAndTermHeadline = () => {
    if (creditType) {
      if (updatedGrid) {
        if (hideDefaultsPreEngagement && !hasUpdatedWallet) {
          return renderDefaultUpdatedGridHeadline({
            logo: 'credit_&_term',
            title: 'Credit & Term',
            subLabelText: 'Select a range',
            iconUrl,
            showIcon,
            isDesktop
          });
        }
        return renderUpdatedGridHeadlineWithData({
          logo: 'credit_&_term',
          title: 'Credit & Term',
          value: creditTypeLabel.label,
          isCurrency: false,
          iconUrl,
          showIcon,
          isDesktop,
          updatedGrid
        });
      }
      return /*#__PURE__*/jsxs(Fragment, {
        children: [/*#__PURE__*/jsx(Text, {
          size: 500,
          color: "accent",
          weight: "bold",
          className: "display-block text-nowrap",
          children: creditTypeLabel.label
        }), /*#__PURE__*/jsx(Text, {
          size: 400,
          color: "accent",
          children: creditTypeLabel.subLabel
        }), renderCreditVerified()]
      });
    }
    if (updatedGrid) {
      return renderDefaultUpdatedGridHeadline({
        logo: 'credit_&_term',
        title: 'Credit & Term',
        subLabelText: 'Select a range',
        iconUrl,
        showIcon,
        isDesktop
      });
    }
    return defaultTitle(`Select a ${updatedGrid ? '' : 'credit'} range`);
  };
  const renderMilesPerYearHeadline = () => {
    if (updatedGrid) {
      return renderUpdatedGridHeadlineWithData({
        logo: 'mileage',
        title: 'MILES PER YEAR',
        value: formatNumber({
          value: leaseMileageRange
        }),
        isCurrency: false,
        iconUrl,
        showIcon,
        isDesktop,
        updatedGrid
      });
    }
    return /*#__PURE__*/jsxs(Fragment, {
      children: [/*#__PURE__*/jsx(Text, {
        size: 500,
        color: "accent",
        weight: "bold",
        className: "display-block",
        children: formatNumber({
          value: leaseMileageRange
        })
      }), /*#__PURE__*/jsxs("span", {
        className: "padding-left-1 text-size-200",
        children: [creditTypeLabel.label, " ", creditTypeLabel.subLabel]
      })]
    });
  };
  const handleTradeInOnClick = useCallback(event => {
    const tradeInPanel = () => {
      if (hasNewTradeIn) {
        return 'valuationHistory';
      }
      // if no icos or trade in value
      if (!(ico && !!ico.length) && !tradeInValue) {
        return 'kbbValuation';
      }
      return 'tradeIn';
    };
    onClick(event, {
      panel: tradeInPanel()
    });
  }, [hasNewTradeIn, ico, onClick, tradeInValue]);
  const handleDownPaymentOnClick = useCallback(event => {
    onClick(event, {
      panel: 'downPayment'
    });
  }, [onClick]);
  const handleCreditScoreOnClick = useCallback(event => {
    onClick(event, {
      panel: 'creditScore'
    });
  }, [onClick]);
  const handleBudgetOnClick = useCallback(event => {
    onClick(event, {
      panel: 'budget'
    });
  }, [onClick]);
  const indicator = /*#__PURE__*/jsx(LoadingSpinner, {
    className: "wallet-grid-spinner display-flex align-items-center margin-0 margin-horizontal-auto",
    height: isDesktop ? desktopContentHeight : lessThanDesktopHeight
  });
  const tradeInTitle = source === 'KBB' ? renderKbbBadge(updatedGrid) : renderButtonTitle(`TRADE-IN ${updatedGrid ? 'VALUE' : ''}`, useKbbLogoTradeIn, updatedGrid);

  // set fixed height to prevent CLS
  const updatedGridProps = {
    contentMinHeight: isDesktop ? '68px' : '104px'
  };
  const creditTileProps = {
    ...(updatedGrid && updatedGridProps),
    title: !isLoading && renderButtonTitle('CREDIT & TERM', useKbbLogoTradeIn, updatedGrid),
    headline: isLoading ? indicator : renderCreditAndTermHeadline(),
    globalWalletTheme: enableKbbBrandedWalletWithoutVariants,
    onClick: handleCreditScoreOnClick,
    dataCmp: 'credit-type-tile'
  };
  const milesPerYearProps = {
    ...(updatedGrid && updatedGridProps),
    title: !isLoading && renderButtonTitle('MILES PER YEAR', useKbbLogoTradeIn, updatedGrid),
    headline: isLoading ? indicator : renderMilesPerYearHeadline(),
    globalWalletTheme: enableKbbBrandedWalletWithoutVariants,
    onClick: handleCreditScoreOnClick,
    dataCmp: 'miles-per-year-tile'
  };
  const shouldTradeInShowLoadingIndicator = isLoading && !enablePaymentOption || isLoading && enablePaymentOption && !isPaymentsInfoLoaded;
  const itemProps = [{
    ...(isLeasePaymentOptionSelected ? milesPerYearProps : creditTileProps)
  }, {
    ...(updatedGrid && updatedGridProps),
    title: !isLoading && renderButtonTitle('BUDGET', useKbbLogoTradeIn, updatedGrid),
    headline: isLoading ? indicator : renderBudgetHeadline(),
    globalWalletTheme: enableKbbBrandedWalletWithoutVariants,
    onClick: handleBudgetOnClick,
    isCurrency: budget !== null && budget >= 0 && !updatedGrid && !isLoading && !enableWalletTileArrangement,
    dataCmp: 'budget-tile'
  }, {
    ...(updatedGrid && updatedGridProps),
    title: !isLoading && renderButtonTitle(isLeasePaymentOptionSelected ? 'DUE AT SIGNING' : 'DOWN PAYMENT', useKbbLogoTradeIn, updatedGrid),
    globalWalletTheme: enableKbbBrandedWalletWithoutVariants,
    onClick: handleDownPaymentOnClick,
    ...downPaymentComponentProps,
    headline: isLoading ? indicator : downPaymentComponentProps.headline,
    headlineClassName: isLoading ? '' : downPaymentComponentProps.headlineClassName,
    isCurrency: isLoading ? false : downPaymentComponentProps.isCurrency,
    dataCmp: isLeasePaymentOptionSelected ? 'due-at-signing-tile' : 'down-payment-tile'
  }, {
    ...(updatedGrid && updatedGridProps),
    title: !shouldTradeInShowLoadingIndicator && tradeInTitle,
    // null checks used here so the default value of null doesnt get coerced into '0'. We want to display 'get started' and not '$0'.
    headline: shouldTradeInShowLoadingIndicator ? indicator : renderTradeInHeadline({
      tradeInValue,
      netTradeInValue,
      useKbbLogoTradeIn,
      updatedGrid,
      source,
      showIcon,
      isDesktop
    }),
    globalWalletTheme: enableKbbBrandedWalletWithoutVariants,
    onClick: handleTradeInOnClick,
    headlineClassName: tradeInValue !== null && tradeInValue >= 0 && !updatedGrid && !shouldTradeInShowLoadingIndicator ? 'text-size-500 text-accent text-bold' : '',
    isCurrency: tradeInValue !== null && tradeInValue >= 0 && !updatedGrid && !shouldTradeInShowLoadingIndicator,
    badge: icoEnabled && !!ico.length && !hasOpenedTradeIn || hasNewTradeIn ? '1' : null,
    source,
    addPadding: !useKbbLogoTradeIn && !updatedGrid,
    dataCmp: 'trade-in-tile'
  }];
  const badge = itemProps[3]?.badge;
  let Component = React.Fragment;
  let componentProps = {};
  if (badge && onTradeInIndicatorImpression) {
    Component = DelayedImpressionObserver;
    componentProps = {
      onChange: onTradeInIndicatorImpression,
      key: 'trade-in-indicator-delayed-impression'
    };
  }
  if (enableWalletTileArrangement) {
    swapElements(itemProps, 0, 1);
  }
  return /*#__PURE__*/jsx(Component, {
    ...componentProps,
    children: /*#__PURE__*/jsx(Row, {
      className: "display-flex flex-wrap",
      "data-cmp": "myWalletGrid",
      children: getItems(itemProps).map(item => /*#__PURE__*/jsx(MyWalletGridTiles, {
        xs: isRow ? 3 : 6,
        tileProps: item,
        className: "text-center",
        ...rest
      }, item.dataCmp))
    })
  });
});
export { MyWalletGrid as default };
